// Only presentational MDMA is accepted. Model output never becomes HTML or URLs.
import { marked } from 'marked'
const chartVariants = ['bar', 'pie', 'line', 'area']

export function csvRows(source) {
  const rows = []
  let row = []
  let value = ''
  let quoted = false
  for (let i = 0; i < source.length; i++) {
    const c = source[i]
    if (c === '"') {
      if (quoted && source[i + 1] === '"') {
        value += '"'
        i++
      } else quoted = !quoted
    } else if (!quoted && (c === ',' || c === '\n')) {
      row.push(value)
      value = ''
      if (c === '\n') {
        if (row.some(Boolean)) rows.push(row)
        row = []
      }
    } else if (c !== '\r') value += c
  }
  if (quoted) throw new Error('Unclosed CSV quote')
  row.push(value)
  if (row.some(Boolean)) rows.push(row)
  return rows
}

export function readWidget(value) {
  if (!value || typeof value !== 'object') throw new Error('Invalid component')
  // The agent sometimes uses the supported variant as the component type.
  // Accept only an unambiguous alias; all chart/data validation still applies.
  if (chartVariants.includes(value.type)) {
    if (value.variant && value.variant !== value.type) throw new Error('Conflicting chart type')
    value = { ...value, type: 'chart', variant: value.type }
  }
  if (value.type === 'callout')
    return {
      type: 'callout',
      title: String(value.title || value.label || ''),
      content: String(value.content || ''),
      variant: ['warning', 'error', 'success'].includes(value.variant)
        ? value.variant
        : 'info',
    }
  if (value.type === 'table') {
    if (!Array.isArray(value.columns) || !Array.isArray(value.data))
      throw new Error('Invalid table')
    return {
      type: 'table',
      title: String(value.label || value.title || 'Data'),
      columns: value.columns.map((c) => ({
        key: String(c.key),
        label: String(c.header || c.key),
      })),
      rows: value.data.map((r) =>
        value.columns.map((c) => String(r[c.key] == null ? '' : r[c.key]))
      ),
    }
  }
  if (
    value.type !== 'chart' ||
    !chartVariants.includes(value.variant)
  )
    throw new Error('Unsupported component')
  const data = csvRows(String(value.data || ''))
  const headers = data.shift() || []
  const x = headers.indexOf(value.xAxis || headers[0])
  const columns =
    (typeof value.yAxis === 'string' ? [value.yAxis] : value.yAxis) ||
    headers.filter((h, i) => i !== x)
  if (
    x < 0 ||
    !Array.isArray(columns) ||
    !columns.length ||
    columns.some((c) => !headers.includes(c)) ||
    !data.length
  )
    throw new Error('Invalid chart columns')
  const series = columns.map((name) => ({
    name,
    values: data.map((r) => {
      const cell = r[headers.indexOf(name)]
      if (cell == null) throw new Error('Missing chart column')
      if (!cell.trim() || cell.trim() === 'null') return null
      if (!Number.isFinite(Number(cell))) throw new Error('Invalid chart number')
      return Number(cell)
    }),
  }))
  if (
    value.variant === 'pie' &&
    series.some(
      (s) => s.values.some((n) => n === null || n < 0) || s.values.every((n) => n === 0)
    )
  )
    throw new Error('Invalid pie chart values')
  return {
    type: 'chart',
    variant: value.variant,
    unit: value.unit === '%' ? '%' : '',
    title: String(value.label || value.title || 'Chart'),
    labels: data.map((r) => r[x]),
    series,
  }
}

export function answerBlocks(source) {
  return marked.lexer(String(source || '')).map((token) => {
    if (token.type === 'code' && /^(mdma|json)(\s+(mdma|json))?$/.test(token.lang || '')) {
      try {
        return { kind: 'widget', widget: readWidget(JSON.parse(token.text)) }
      } catch (_) {
        return {
          kind: 'unavailable',
          text: 'This visual could not be displayed. Ask Lynda to show the underlying figures.',
        }
      }
    }
    return { kind: 'markdown', token }
  })
}
